import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, X, Users, MessageCircle } from 'lucide-react';
import ChatService from '../../services/chatService';

const ChatRoom = ({ ticketId, user, onClose, onNewMessage }) => {
  // ALL HOOKS MUST BE DECLARED FIRST - NO CONDITIONAL EXECUTION
  const [chatRoom, setChatRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [newMessageNotification, setNewMessageNotification] = useState(false);
  
  const messagesEndRef = useRef(null);
  const messageContainerRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const isPollingRef = useRef(false);
  const lastMessageIdRef = useRef(null);
  const componentMountedRef = useRef(true);

  // Initialize chat room
  useEffect(() => {
    componentMountedRef.current = true;
    
    if (ticketId && user && !initialized) {
      initializeChatRoom();
    } else {
      if (!ticketId) {
        setError('No ticket ID provided');
        setLoading(false);
      }
      if (!user) {
        setError('User not authenticated');
        setLoading(false);
      }
    }

    return () => {
      componentMountedRef.current = false;
    };
  }, [ticketId, user, initialized]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (initialized && messages.length > 0) {
      scrollToBottom();
      
      // Update last message ID
      if (messages.length > 0) {
        lastMessageIdRef.current = messages[messages.length - 1].id;
      }
    }
  }, [messages, initialized]);

  // FIXED: Controlled polling without recursion
  useEffect(() => {
    if (initialized && chatRoom && !isPollingRef.current) {
      startPolling();
      
      return () => {
        stopPolling();
      };
    }
  }, [initialized, chatRoom]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      componentMountedRef.current = false;
    };
  }, []);

  const startPolling = useCallback(() => {
    if (isPollingRef.current || !chatRoom) return;
    
    isPollingRef.current = true;
    pollIntervalRef.current = setInterval(() => {
      if (componentMountedRef.current && !sendingMessage) {
        pollForNewMessages();
      }
    }, 8000); // Poll every 8 seconds
  }, [chatRoom, sendingMessage]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  const initializeChatRoom = useCallback(async () => {
    if (!componentMountedRef.current) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get or create chat room
      const roomResponse = await ChatService.getTicketChatRoom(ticketId);
      
      if (!componentMountedRef.current) return;
      
      if (!roomResponse?.data?.chat_room) {
        throw new Error('Invalid chat room response');
      }
      
      const room = roomResponse.data.chat_room;
      setChatRoom(room);
      
      // Load initial messages
      const messagesResponse = await ChatService.getMessages(room.id);
      
      if (!componentMountedRef.current) return;
      
      if (!messagesResponse?.data) {
        throw new Error('Invalid messages response');
      }
      
      const messagesList = messagesResponse.data.messages || [];
      // Ensure unique messages and add client-side unique keys
      const uniqueMessages = messagesList.reverse().map((msg, index) => ({
        ...msg,
        clientKey: `${msg.id}-${index}-initial`
      }));
      
      setMessages(uniqueMessages);
      setPagination(messagesResponse.data.pagination || null);
      
      // Set last message ID for polling
      if (uniqueMessages.length > 0) {
        lastMessageIdRef.current = uniqueMessages[uniqueMessages.length - 1].id;
      }
      
      // Mark as read
      try {
        await ChatService.markAsRead(room.id);
      } catch (markReadError) {
        console.warn('Could not mark as read:', markReadError);
      }
      
      setInitialized(true);
      
    } catch (err) {
      if (componentMountedRef.current) {
        console.error('Error initializing chat:', err);
        setError(err.message || 'Failed to load chat');
      }
    } finally {
      if (componentMountedRef.current) {
        setLoading(false);
      }
    }
  }, [ticketId]);

  // FIXED: Non-recursive polling with proper state management and user check
  const pollForNewMessages = useCallback(async () => {
    if (!chatRoom || !componentMountedRef.current || sendingMessage || !user?.id) return;

    try {
      const result = await ChatService.checkForNewMessages(chatRoom.id);
      
      if (!componentMountedRef.current || !result) return;

      const { newMessages } = result;
      
      if (newMessages && newMessages.length > 0) {
        // Filter out messages we already have
        setMessages(prevMessages => {
          const existingIds = new Set(prevMessages.map(m => m.id));
          const trulyNewMessages = newMessages.filter(msg => !existingIds.has(msg.id));
          
          if (trulyNewMessages.length === 0) {
            return prevMessages;
          }
          
          // Add unique client keys to new messages
          const uniqueNewMessages = trulyNewMessages.map((msg, index) => ({
            ...msg,
            clientKey: `${msg.id}-poll-${Date.now()}-${index}`
          }));
          
          // Trigger notification for messages from other users
          const otherUserMessages = uniqueNewMessages.filter(msg => msg.user_id !== user?.id);
          if (otherUserMessages.length > 0) {
            triggerNewMessageNotification();
            if (onNewMessage) {
              otherUserMessages.forEach(msg => onNewMessage(msg));
            }
          }
          
          return [...prevMessages, ...uniqueNewMessages];
        });
        
        // Mark as read if the chat is visible
        try {
          await ChatService.markAsRead(chatRoom.id);
        } catch (error) {
          console.warn('Could not mark as read:', error);
        }
      }
    } catch (error) {
      console.warn('Error polling for messages:', error);
    }
  }, [chatRoom, user?.id, onNewMessage, sendingMessage]);

  const triggerNewMessageNotification = useCallback(() => {
    if (!componentMountedRef.current) return;
    
    setNewMessageNotification(true);
    setTimeout(() => {
      if (componentMountedRef.current) {
        setNewMessageNotification(false);
      }
    }, 3000);
  }, []);

  const loadMoreMessages = useCallback(async () => {
    if (!pagination || pagination.current_page >= pagination.total_pages || loadingMore) {
      return;
    }

    try {
      setLoadingMore(true);
      
      const response = await ChatService.getMessages(chatRoom.id, pagination.current_page + 1);
      
      if (!componentMountedRef.current) return;
      
      const olderMessages = response.data.messages.reverse().map((msg, index) => ({
        ...msg,
        clientKey: `${msg.id}-older-${index}-${Date.now()}`
      }));
      
      setMessages(prev => [...olderMessages, ...prev]);
      setPagination(response.data.pagination);
    } catch (err) {
      if (componentMountedRef.current) {
        console.error('Error loading more messages:', err);
        setError('Failed to load more messages');
      }
    } finally {
      if (componentMountedRef.current) {
        setLoadingMore(false);
      }
    }
  }, [chatRoom, pagination, loadingMore]);

  // FIXED: Prevent duplicate message sending with user check
  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || sendingMessage || !componentMountedRef.current || !user?.id) {
      return;
    }
    
    const messageText = newMessage.trim();
    setNewMessage(''); // Clear input immediately
    setSendingMessage(true);
    
    // Temporarily stop polling to prevent conflicts
    const wasPolling = isPollingRef.current;
    if (wasPolling) {
      stopPolling();
    }
    
    try {
      setError(null);
      
      const messageData = {
        message: messageText,
        message_type: 'text'
      };
      
      const response = await ChatService.sendMessage(chatRoom.id, messageData);
      
      if (!componentMountedRef.current) return;
      
      // Add sent message to state if it doesn't already exist
      setMessages(prevMessages => {
        const messageExists = prevMessages.some(msg => msg.id === response.data.message.id);
        
        if (messageExists) {
          return prevMessages;
        }
        
        const sentMessage = {
          id: response.data.message.id,
          chat_room_id: response.data.message.chat_room_id,
          user_id: response.data.message.user_id,
          message: response.data.message.message,
          message_type: response.data.message.message_type,
          attachments: response.data.message.attachments || [],
          is_system_message: response.data.message.is_system_message,
          user: response.data.message.user,
          created_at: response.data.message.created_at,
          clientKey: `${response.data.message.id}-sent-${Date.now()}`
        };
        
        return [...prevMessages, sentMessage];
      });
      
    } catch (err) {
      if (componentMountedRef.current) {
        console.error('Error sending message:', err);
        setError(err.message || 'Failed to send message');
        setNewMessage(messageText); // Restore message on error
      }
    } finally {
      if (componentMountedRef.current) {
        setSendingMessage(false);
        
        // Restart polling after a short delay
        setTimeout(() => {
          if (componentMountedRef.current && wasPolling) {
            startPolling();
          }
        }, 1000);
      }
    }
  }, [newMessage, sendingMessage, user?.id, chatRoom, stopPolling, startPolling]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (componentMountedRef.current && messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, []);

  const handleScroll = useCallback((e) => {
    const { scrollTop } = e.target;
    if (scrollTop === 0 && pagination && pagination.current_page < pagination.total_pages) {
      loadMoreMessages();
    }
  }, [pagination, loadMoreMessages]);

  const handleRetry = useCallback(() => {
    setInitialized(false);
    setError(null);
    setMessages([]);
    setChatRoom(null);
    lastMessageIdRef.current = null;
    stopPolling();
    initializeChatRoom();
  }, [stopPolling, initializeChatRoom]);

  // MOVED USER VALIDATION AFTER HOOKS - NOW CHECK AND RETURN EARLY IF NO USER
  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-white rounded-lg shadow-sm border">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4 mx-auto"></div>
          <span className="text-gray-600">Loading user information...</span>
          <span className="text-sm text-gray-500 mt-2">Please wait while we authenticate your session</span>
        </div>
      </div>
    );
  }

  // Enhanced Loading state
  if (loading && !initialized) {
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-white rounded-lg shadow-sm border">
        {/* Modern loading animation */}
        <div className="relative mb-6">
          <div className="w-16 h-16 border-4 border-blue-100 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-blue-600 rounded-full animate-spin border-t-transparent"></div>
        </div>
        
        {/* Loading dots animation */}
        <div className="flex space-x-1 mb-4">
          <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
          <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse delay-100"></div>
          <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse delay-200"></div>
        </div>
        
        {/* Progressive text */}
        <div className="text-center space-y-2">
          <h3 className="text-lg font-medium text-gray-900">Connecting to chat</h3>
          <div className="text-sm text-gray-600 space-y-1">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span>Verifying access permissions</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span>Establishing secure connection</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 border-2 border-blue-600 rounded-full animate-spin border-t-transparent"></div>
              <span>Loading chat history</span>
            </div>
          </div>
        </div>
        
        <button 
          onClick={handleRetry}
          className="mt-6 px-4 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
        >
          Having trouble? Click to retry
        </button>
      </div>
    );
  }

  // Error state
  if (error && !chatRoom) {
    return (
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <X className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  Unable to load chat
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {error}
                </div>
                <div className="mt-3">
                  <button
                    onClick={handleRetry}
                    className="text-sm bg-red-100 text-red-800 px-3 py-2 rounded hover:bg-red-200 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-sm border">
      {/* New Message Notification */}
      {newMessageNotification && (
        <div className="absolute top-4 right-4 z-50 animate-pulse">
          <div className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2">
            <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
            <span className="text-sm font-medium">New message</span>
          </div>
        </div>
      )}

      {/* Chat Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <div className="flex items-center space-x-3">
          <MessageCircle className="h-5 w-5 text-blue-600" />
          <div>
            <h3 className="font-medium text-gray-900">{chatRoom?.name}</h3>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Users className="h-4 w-4" />
              <span>{chatRoom?.participants?.length || 0} participants</span>
              {chatRoom?.ticket_id && (
                <span className="text-blue-600">• Ticket #{chatRoom.ticket_id}</span>
              )}
            </div>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded"
            title="Close chat"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Messages Container */}
      <div 
        ref={messageContainerRef}
        className="flex-1 overflow-y-auto p-4"
        onScroll={handleScroll}
      >
        {loadingMore && (
          <div className="text-center py-2 mb-4">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
            <span className="text-xs text-gray-500 mt-1">Loading older messages...</span>
          </div>
        )}
        
        {/* Empty state */}
        {messages.length === 0 && !loadingMore && (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <MessageCircle className="h-12 w-12 text-gray-300 mb-4" />
            <h4 className="text-lg font-medium text-gray-600 mb-2">Start the conversation</h4>
            <p className="text-gray-500 mb-4 max-w-sm">
              This is the beginning of your support chat. Send a message to get help with your ticket.
            </p>
            {chatRoom?.ticket_id && (
              <div className="text-sm text-gray-400 bg-gray-50 px-3 py-2 rounded-lg">
                Ticket #{chatRoom.ticket_id}
              </div>
            )}
          </div>
        )}
        
        {/* Messages - FIXED: Use clientKey for unique keys */}
        {messages.length > 0 && (
          <div className="space-y-4">
            {messages.map((message) => (
              <MessageBubble 
                key={message.clientKey || `${message.id}-${message.created_at}`}
                message={message} 
                currentUser={user}
              />
            ))}
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Temporary Error Display */}
      {error && chatRoom && (
        <div className="mx-4 mb-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <X className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
            <span className="ml-2 text-sm text-red-700 flex-1">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-2 text-red-400 hover:text-red-600 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className="border-t p-4">
        <form onSubmit={handleSendMessage} className="flex items-end space-x-2">
          <div className="flex-1">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              rows="2"
              maxLength="2000"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              disabled={sendingMessage}
            />
            <div className="mt-1 text-xs text-gray-500 text-right">
              {newMessage.length}/2000
            </div>
          </div>
          
          <button
            type="submit"
            disabled={!newMessage.trim() || sendingMessage}
            className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Send message"
          >
            {sendingMessage ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </form>
        
        <div className="mt-2 text-xs text-gray-400 text-center">
          Press Enter to send, Shift+Enter for new line
        </div>
      </div>
    </div>
  );
};

// Message Bubble Component - FIXED: Add user check
const MessageBubble = ({ message, currentUser }) => {
  // Add safety check for currentUser
  const isOwnMessage = currentUser?.id && message.user_id === currentUser.id;
  const isSystemMessage = message.is_system_message;

  if (isSystemMessage) {
    return (
      <div className="flex justify-center">
        <div className="bg-blue-50 text-blue-700 text-sm px-4 py-2 rounded-full border border-blue-200">
          {message.message}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg ${
        isOwnMessage 
          ? 'bg-blue-600 text-white' 
          : 'bg-gray-100 text-gray-900'
      }`}>
        {!isOwnMessage && (
          <div className={`text-xs font-medium mb-1 ${
            isOwnMessage ? 'text-blue-100' : 'text-gray-600'
          }`}>
            {message.user?.name || 'User'}
          </div>
        )}
        
        <div className="text-sm whitespace-pre-wrap">
          {message.message}
        </div>
        
        <div className={`text-xs mt-2 ${
          isOwnMessage ? 'text-blue-100' : 'text-gray-500'
        }`}>
          {ChatService.formatMessageTime(message.created_at)}
        </div>
      </div>
    </div>
  );
};

export default ChatRoom;